import type { ReactNode } from "react";
import { X } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { cn } from "@/lib/utils";
import { formatElapsed } from "@/lib/elapsed";
import type { SessionTone } from "@/lib/multi-view";

const DOT: Record<SessionTone, string> = {
  cooking: "bg-(--brand-blue)",
  late: "bg-(--color-danger)",
  onDeck: "bg-(--gray-400)",
  paused: "bg-(--color-warning)",
};

export function StatusDot({ tone, className }: { tone: SessionTone; className?: string }) {
  return <span aria-hidden className={cn("size-2.5 shrink-0 rounded-full", DOT[tone], className)} />;
}

interface SessionChipProps {
  name: string;
  tone: SessionTone;
  /** Live seconds for the timer; left out where the chip carries no clock (Prep). */
  seconds?: number;
  /** Makes the chip a tab: pressed when `active`. */
  onSelect?: () => void;
  active?: boolean;
  /** Shows the X; `removeLabel` is its accessible name. */
  onRemove?: () => void;
  trailing?: ReactNode;
  className?: string;
}

/**
 * A recipe as a pill: a status dot, its name and, in Multi View, its live
 * timer. On Prep it only names what is ticked; in the strip and the tabs it is
 * the control that picks the recipe.
 */
export function SessionChip({ name, tone, seconds, onSelect, active, onRemove, trailing, className }: SessionChipProps) {
  const { t } = useLingui();
  const body = (
    <>
      <StatusDot tone={tone} />
      <span className="max-w-40 truncate text-sm font-medium">{name}</span>
      {seconds !== undefined && (
        <span
          className={cn(
            "text-xs font-semibold tabular-nums",
            tone === "late" ? "text-(--color-danger-text)" : "text-(--color-text-secondary)",
          )}
        >
          {formatElapsed(seconds)}
        </span>
      )}
      {trailing}
    </>
  );
  const surface = cn(
    "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-(--color-text-primary) transition-colors",
    active
      ? "border-(--color-action) bg-(--color-surface-accent)"
      : "border-(--color-border) bg-(--color-surface-muted)",
    className,
  );

  return (
    <span className={cn("inline-flex items-center", onRemove && "gap-0")}>
      {onSelect ? (
        <button
          type="button"
          role="tab"
          aria-selected={!!active}
          onClick={onSelect}
          className={cn(surface, "cursor-pointer hover:bg-(--color-surface-accent)", onRemove && "rounded-r-none border-r-0")}
        >
          {body}
        </button>
      ) : (
        <span className={cn(surface, onRemove && "rounded-r-none border-r-0")}>{body}</span>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={t`Remove ${name} from this view`}
          className={cn(
            "inline-flex cursor-pointer items-center self-stretch rounded-r-full border border-l-0 px-2 text-(--color-text-secondary) hover:text-(--color-text-primary)",
            active
              ? "border-(--color-action) bg-(--color-surface-accent)"
              : "border-(--color-border) bg-(--color-surface-muted)",
          )}
        >
          <X className="size-3.5" />
        </button>
      )}
    </span>
  );
}
